import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import qs from "query-string";
import some from "lodash/some";

import { Trans } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";

import dayjs from "../../lib/dayjs";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useLanguage } from "../../bit/language";

import Modal from "../../components/Modal";
import AggrementDialog from "../../components/Aggrement/AggrementDialog";
import PhotoDialog from "../../components/Photo/PhotoDialog";

import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useAgreementState } from "../../lib/hooks/useAgreementState";
import { useAcceptedAgreements } from "../../context/Agreement";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";
import { useAccessControlAutoSwitch } from "../../lib/hooks/useAccessControlAutoSwitch";
import { useTeamsCallerTokenAndTargetTeamsUserIds } from "../../lib/hooks/useTeamsCallerTokenAndTargetTeamsUserIds";
import {
  useClientPlan,
  useDigitalReceptionMetadata,
  useHidScannerSettings,
  usePhotoCaptureSettings,
  usePrintingEnabled,
  useAccessSettings,
  useTeamsVideoCallEnabled,
} from "../../lib/hooks/useQueryBoard";

// import useEventTracking from "../../bit/tracking/useEventTracking";

import { MUTATION_ACCESS_SCAN } from "../../config/graphql/mutation";
import {
  QUERY_ACCESS_EVENT,
  QUERY_MEMBER_CODE_CHECK,
} from "../../config/graphql/query";

import { useBrotherPrinter } from "../../context/BrotherPrinter";
import { useScannerContext } from "../../context/Scanner";
import Logger from "../../lib/logger";
import { beepError, beepSuccess } from "../../lib/scanFeedback";

interface ScanningProps {
  photo: string | null;
}

const Scanning = React.memo(({ photo }: ScanningProps) => {
  const navigate = useNavigate();

  const { code, action } = useParams<{
    code: string;
    action: "checkin" | "checkout";
  }>();

  const [searchParams] = useSearchParams();

  const location = useLocation();

  const locationCustomFieldValues = (
    location.state as { customFieldValues?: Record<string, any> } | null
  )?.customFieldValues;

  /**
   * Determines whether the current scan should be treated as NFC scanning.
   *
   * - When scanning via the Prodvx kiosk, NFC scans explicitly pass `isNFC=true`
   *   as a query parameter.
   *
   * - In the future, when NFC scanning is implemented via the Castit Wayfinding app,
   *   the same `isNFC=true` flag will be used. (This is currently NOT implemented,
   *   as devices will rely on NFC readers that behave as keyboard emulators.)
   *
   * - When using keyboard-emulated NFC readers, no explicit NFC flag is sent.
   *   In that case, we infer NFC scanning based on the scanned value:
   *   currently, only NFC UUIDs differ from standard PIN codes by having
   *   a length other than 6 characters.
   *
   * - At the moment, there are no other known non-6-character scan values
   *   besides NFC UUIDs. If additional formats are introduced in the future,
   *   this logic should be revisited and extended accordingly.
   */
  const isNfcScanning =
    searchParams.get("isNFC") === "true" || (!!code && code.length !== 6);

  const clientPlan = useClientPlan();

  const queryClient = useQueryClient();
  const printingEnabled = usePrintingEnabled();
  const { print } = useBrotherPrinter();
  const receptionMetadata = useDigitalReceptionMetadata();
  const { isOnlyQrScannerMode } = useHidScannerSettings();

  const {
    isAccessAllowedForVisitorsForCheckin,
    isAccessAllowedForGuestsForCheckin,
    isAccessAllowedForEmployeesForCheckin,
    isAccessAllowedForMeetingAttendeesForCheckin,
    isAccessEnabledForAutoOpenForCheckin,
    isAccessAllowedForVisitorsForCheckout,
    isAccessAllowedForGuestsForCheckout,
    isAccessAllowedForEmployeesForCheckout,
    isAccessAllowedForMeetingAttendeesForCheckout,
    isAccessEnabledForAutoOpenForCheckout,
    accessController,
  } = useAccessSettings();

  const {
    trigger: triggerAutoSwitch,
    doorStatus,
    doorModal,
  } = useAccessControlAutoSwitch();

  // const { trackEvent } = useEventTracking();

  const { screen } = useAgreementState();
  const { accepted: acceptedAgreements } = useAcceptedAgreements();

  const [meetingAccessError, setMeetingAccessError] = useState<{
    kind: "tooEarly" | "tooLate";
    values: Record<string, string>;
  } | null>(null);

  const formatTime = (iso?: string) => (iso ? dayjs(iso).format("HH:mm") : "");

  const { data, status: requestStatus } = useQuery(
    [`scan/${code}`],
    async () => {
      let input: any = {
        code,
      };

      if (action) {
        input = {
          ...input,
          action: action === "checkout" ? "checkOut" : "checkIn",
        };
      }

      /**
       * Only the documents this person went through, not every document the
       * screen has — the server writes one signing record per id it gets.
       */
      if (acceptedAgreements.length > 0) {
        input = {
          ...input,
          acceptedAgreements,
          screen,
        };
      }

      if (photo) {
        input = {
          ...input,
          photo,
        };
      }

      if (locationCustomFieldValues) {
        input = {
          ...input,
          customFieldValues: locationCustomFieldValues,
        };
      }

      const response = await client.request<{ accessScan: IAccessEvent }>(
        MUTATION_ACCESS_SCAN,
        {
          input,
        },
      );

      const accessScanId = response?.accessScan?.id;

      /**
       * Prefetch access event so we don't wait when on contact page
       */
      await queryClient
        .prefetchQuery([`access/event/${accessScanId}`], () =>
          client.request(QUERY_ACCESS_EVENT, { id: accessScanId }),
        )
        .catch(() => null);

      return response;
    },
    {
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      onError(error: any) {
        const graphqlErrors = error?.response?.errors as
          | {
              extensions?: {
                code?: string;
                fields?: any[];
                validFrom?: string;
                validUntil?: string;
                dtstart?: string;
                dtend?: string;
              };
            }[]
          | undefined;

        const preregError = graphqlErrors?.find(
          (e) => e?.extensions?.code === "MISSING_PREREG_FIELDS",
        );

        if (preregError && code) {
          const fields = preregError.extensions?.fields ?? [];

          navigate(`/access/checkin/missing-fields/${code}`, {
            replace: true,
            state: { fields, photo, action },
          });
          return;
        }

        const meetingError = graphqlErrors?.find(
          (e) =>
            e?.extensions?.code === "MEETING_CODE_TOO_EARLY" ||
            e?.extensions?.code === "MEETING_CODE_TOO_LATE",
        );

        if (meetingError) {
          const ext = meetingError.extensions ?? {};
          const kind =
            ext.code === "MEETING_CODE_TOO_EARLY" ? "tooEarly" : "tooLate";

          setMeetingAccessError({
            kind,
            values: {
              validFromTime: formatTime(ext.validFrom),
              validUntilTime: formatTime(ext.validUntil),
              meetingStartTime: formatTime(ext.dtstart),
              meetingEndTime: formatTime(ext.dtend),
            },
          });
        }
      },
    },
  );

  const status = useDebounceStatus(requestStatus, 1500);

  const { qrSocket } = useScannerContext();

  const scanResultEmittedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!qrSocket || !code) {
      return;
    }

    if (status !== "success" && status !== "error") {
      return;
    }

    const key = `${code}:${status}`;

    if (scanResultEmittedRef.current === key) {
      return;
    }

    scanResultEmittedRef.current = key;
    qrSocket.emit("scan-result", {
      result: status === "success" ? "good" : "bad",
    });
  }, [qrSocket, status, code]);

  const feedbackPlayedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOnlyQrScannerMode) {
      return;
    }

    if (!status || status === "loading" || status === "idle") {
      return;
    }

    const key = `${code}:${status}`;

    if (feedbackPlayedRef.current === key) {
      return;
    }

    feedbackPlayedRef.current = key;

    if (status === "success") {
      Logger.info("[scan] success", { code, action });
      beepSuccess();
    } else if (status === "error") {
      Logger.warn("[scan] error", { code, action });
      beepError();
    }
  }, [status, code, action, isOnlyQrScannerMode]);

  const id = data?.accessScan?.id;
  const type = data?.accessScan?.type;
  const metadata = data?.accessScan?.metadata;
  const ownerId = data?.accessScan?.owner?.id;

  const receiver = data?.accessScan?.receiver;
  const receiverEmail = receiver?.email;

  const autoEmail =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.email;
  const autoSMS =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.sms;
  const autoCall =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.call;
  const autoPing =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.ping;
  const autoVideoCall =
    !!receiver?.availablePreferences?.meetingAttendee?.autoChannel?.videoCall &&
    !!data?.accessScan?.videoRoom;
  const autoTeamsCall =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.teamsCall;

  const direction = useMemo(() => {
    if (data) {
      return !!(metadata && "checkOut" in metadata) ? "checkout" : "checkin";
    }

    return action || "checkin";
  }, [data]);

  const translationKey = useMemo(() => {
    const key = `check:status.${status}`;

    if (!(status === "success" && type)) {
      if (status === "error" && meetingAccessError) {
        return `check:status.error.meeting.${meetingAccessError.kind}`;
      }

      if (isNfcScanning) {
        return `${key}.nfc`;
      }

      return key;
    }

    const returnType = type === "Guest" ? "Visitor" : type;

    if (direction === "checkout") {
      if (
        !(isAccessEnabledForAutoOpenForCheckout && accessController) &&
        ((type === "Employee" && isAccessAllowedForEmployeesForCheckout) ||
          (type === "MeetingAttendee" &&
            isAccessAllowedForMeetingAttendeesForCheckout) ||
          (type === "Visitor" && isAccessAllowedForVisitorsForCheckout) ||
          (type === "Guest" && isAccessAllowedForGuestsForCheckout))
      ) {
        return `${key}.${direction}.access`;
      }
      return `${key}.${direction}.${returnType}`;
    }

    if (printingEnabled) {
      return `${key}.${direction}.${returnType}.badge`;
    }

    if (type !== "MeetingAttendee") {
      return `${key}.${direction}.${returnType}`;
    }

    let messageType = "no-email-sms";

    if (autoCall || autoPing) {
      messageType = "call-ping";
    } else if (autoTeamsCall) {
      messageType = "teams-call";
    } else if (autoVideoCall) {
      messageType = "video-call";
    } else if (autoEmail || autoSMS) {
      messageType = "email-sms";
    }

    return `${key}.${direction}.${returnType}.${messageType}`;
  }, [
    status,
    direction,
    type,
    autoCall,
    autoPing,
    autoEmail,
    autoSMS,
    autoTeamsCall,
    autoVideoCall,
    printingEnabled,
    isAccessAllowedForVisitorsForCheckout,
    isAccessAllowedForGuestsForCheckout,
    isAccessAllowedForEmployeesForCheckout,
    isAccessAllowedForMeetingAttendeesForCheckout,
    isAccessEnabledForAutoOpenForCheckout,
    accessController,
    isNfcScanning,
  ]);

  const teamsVideoCallEnabled = useTeamsVideoCallEnabled();
  const { callerAcsToken, targetTeamsUserIds } =
    useTeamsCallerTokenAndTargetTeamsUserIds({
      emails: receiverEmail ? [receiverEmail] : [],
      enabled: !!autoTeamsCall && !!receiverEmail,
    });

  const onDismiss = useCallback(() => {
    const isFree = clientPlan && clientPlan === "free";
    const isCheckInSuccess =
      id && direction === "checkin" && status === "success";
    const isCheckOutSuccess =
      id && direction === "checkout" && status === "success";

    if (!isFree && isCheckInSuccess) {
      if (type !== "MeetingAttendee") {
        if (receptionMetadata?.enableExternalDoorTrigger) {
          return navigate("/door", { replace: true });
        }

        if (!!receptionMetadata?.accessControl) {
          return navigate(`/door-control/${id}`, { replace: true });
        }

        if (
          (type === "Employee" && isAccessAllowedForEmployeesForCheckin) ||
          (type === "Visitor" && isAccessAllowedForVisitorsForCheckin) ||
          (type === "Guest" && isAccessAllowedForGuestsForCheckin)
        ) {
          if (isAccessEnabledForAutoOpenForCheckin && accessController) {
            return triggerAutoSwitch(accessController);
          }
          return navigate(`/access-control/${accessController}`, {
            replace: true,
          });
        }
      }

      const shouldPrint = printingEnabled && ownerId;

      if (shouldPrint) {
        let printWindow: Window | null = null;

        if (receptionMetadata.printRef === "window") {
          printWindow = window.open("", "");
          window.focus();
        }

        print(ownerId, printWindow);
      }

      if (type === "MeetingAttendee") {
        if (autoCall || autoPing) {
          return navigate(
            `/access/checkin/contact/${id}/${!!autoCall ? "call" : "ping"}`,
            {
              replace: true,
            },
          );
        }

        if (!!autoTeamsCall) {
          return navigate(
            `/${
              teamsVideoCallEnabled
                ? "teams-video"
                : `access/checkin/teams-audio`
            }-call?${qs.stringify({
              callerAcsToken,
              targetTeamsUserIds,
              type: "MeetingAttendee",
            })}`,
            {
              replace: true,
            },
          );
        }

        if (autoVideoCall) {
          return navigate(
            `/video-call?${qs.stringify({
              id,
              type: "MeetingAttendee",
            })}`,
            { state: { videoRoom: data?.accessScan?.videoRoom } },
          );
        }

        // if (!!autoSMS) {
        //   return navigate("/", { replace: true });
        // }

        if (
          some(
            receiver?.availablePreferences?.meetingAttendee?.channel,
            (v) => v === true,
          )
        ) {
          return navigate(`/contact/${id}`, { replace: true });
        }

        if (receptionMetadata?.enableExternalDoorTrigger) {
          return navigate("/door", { replace: true });
        }

        if (!!receptionMetadata?.accessControl) {
          return navigate(`/door-control/${id}`, { replace: true });
        }

        if (isAccessAllowedForMeetingAttendeesForCheckin) {
          if (isAccessEnabledForAutoOpenForCheckin && accessController) {
            return triggerAutoSwitch(accessController);
          }
          return navigate(`/access-control/${accessController}`, {
            replace: true,
          });
        }
      }
    }

    if (!isFree && isCheckOutSuccess) {
      if (
        (type === "Employee" && isAccessAllowedForEmployeesForCheckout) ||
        (type === "MeetingAttendee" &&
          isAccessAllowedForMeetingAttendeesForCheckout) ||
        (type === "Visitor" && isAccessAllowedForVisitorsForCheckout) ||
        (type === "Guest" && isAccessAllowedForGuestsForCheckout)
      ) {
        if (isAccessEnabledForAutoOpenForCheckout && accessController) {
          return triggerAutoSwitch(accessController);
        }
        return navigate(`/access-control/${accessController}`, {
          replace: true,
        });
      }
    }

    /**
     * In case there was an error, try to redirect the user to where he came from in order to retry the action
     */
    if (status === "error") {
      return navigate(-1);
    }

    return navigate("/");
  }, [id, direction, status, type, action]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 3000;
    }

    if (status === "success") {
      return receptionMetadata?.message?.[direction]?.durationMs || 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  const [language] = useLanguage();

  const typeMessages =
    status === "success" &&
    type &&
    receptionMetadata?.message?.[direction]?.[type];

  const localizedTitle =
    typeof typeMessages === "object" &&
    typeof typeMessages.title?.[language] === "string"
      ? typeMessages.title[language]
      : undefined;

  const localizedMessage =
    typeof typeMessages === "object" &&
    typeof typeMessages.message?.[language] === "string"
      ? typeMessages.message[language]
      : undefined;

  if (doorStatus) {
    return doorModal;
  }

  const transValues = meetingAccessError?.values;

  return (
    <Modal
      title={
        localizedTitle ?? (
          <Trans
            // @ts-expect-error
            i18nKey={`${translationKey}.title`}
            values={transValues}
          />
        )
      }
      message={
        localizedMessage ?? (
          <Trans
            // @ts-expect-error
            i18nKey={`${translationKey}.message`}
            values={transValues}
          />
        )
      }
      status={status}
      onDismiss={onDismiss}
    />
  );
});

const PreScanning = () => {
  const { code, action } = useParams();

  const isCheckin = action === "checkin";

  const navigate = useNavigate();

  const preScanLocation = useLocation();

  const seededPhoto =
    (preScanLocation.state as { photo?: string | null } | null)?.photo ?? null;

  const { agreementsFor, hasRestrictedAgreements } = useAgreementState();
  const { accept } = useAcceptedAgreements();

  /**
   * The code identifies the person, so from here on the documents can be
   * narrowed to their member type — including the ones that were deliberately
   * held back on the page before this one.
   */
  const [memberType, setMemberType] = useState<
    EntranceAgreementMemberType | undefined
  >();

  const pendingAgreements = isCheckin ? agreementsFor(memberType) : [];

  const {
    isPhotoCaptureEnabled,
    isPhotoCaptureAllowedForEmployees,
    isPhotoCaptureAllowedForVisitors,
    isPhotoCaptureAllowedForMeetingAttendees,
    isPhotoCaptureRequiredForEmployees,
    isPhotoCaptureRequiredForVisitors,
    isPhotoCaptureRequiredForMeetingAttendees,
  } = usePhotoCaptureSettings();

  const [photoCapture, setPhotoCapture] = useState<boolean>(false);

  const [photoCaptureRequired, setPhotoCaptureRequired] =
    useState<boolean>(false);

  const [photo, setPhoto] = useState<string | null>(seededPhoto);

  const [isMemberTypeAndPhotoChecked, setIsMemberTypeAndPhotoChecked] =
    useState<boolean>(!!seededPhoto);

  const handleUserData = useCallback(
    (userData: {
      getMemberTypeAndPhotoStatusByCode: {
        type: "Employee" | "Visitor" | "MeetingAttendee";
        hasPhoto: boolean;
      };
    }) => {
      const { type, hasPhoto } = userData.getMemberTypeAndPhotoStatusByCode;

      setMemberType(type);

      const typeConfig = {
        Employee: {
          allowed: isPhotoCaptureAllowedForEmployees,
          required: isPhotoCaptureRequiredForEmployees,
        },
        Visitor: {
          allowed: isPhotoCaptureAllowedForVisitors,
          required: isPhotoCaptureRequiredForVisitors,
        },
        MeetingAttendee: {
          allowed: isPhotoCaptureAllowedForMeetingAttendees,
          required: isPhotoCaptureRequiredForMeetingAttendees,
        },
      };

      if (typeConfig[type]?.allowed && !hasPhoto) {
        setPhotoCapture(true);
        setPhotoCaptureRequired(typeConfig[type].required);
      } else {
        setPhotoCapture(false);
        setPhotoCaptureRequired(false);
      }
    },
    [
      isPhotoCaptureAllowedForEmployees,
      isPhotoCaptureAllowedForVisitors,
      isPhotoCaptureAllowedForMeetingAttendees,
      isPhotoCaptureRequiredForEmployees,
      isPhotoCaptureRequiredForVisitors,
      isPhotoCaptureRequiredForMeetingAttendees,
    ],
  );

  const { status } = useQuery(
    [
      "getMemberTypeAndPhotoStatusByCode",
      {
        code,
      },
    ] as const,
    ({ queryKey: [_key, variables] }) =>
      client.request(QUERY_MEMBER_CODE_CHECK, variables),
    {
      /**
       * Resolving the member type also gates the entrance agreements now, so
       * the request is worth making for screens that restrict a document to
       * certain member types even when photo capture is off.
       */
      enabled:
        isCheckin &&
        (isPhotoCaptureEnabled || hasRestrictedAgreements) &&
        !!code &&
        !isMemberTypeAndPhotoChecked,
      retry: false,
      onSuccess: handleUserData,
      onError: () => {
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      },
      onSettled: () => {
        setIsMemberTypeAndPhotoChecked(true);
      },
    },
  );

  /**
   * Photo capture owns the error and loading states of this request; when it is
   * off, the type is a nice-to-have for picking documents and a failure must not
   * stop the scan — accessScan reports the real reason a moment later.
   */
  if (isCheckin && isPhotoCaptureEnabled && status === "error") {
    return (
      <Modal
        title={<Trans i18nKey={`check:status.${status}.title`} />}
        message={<Trans i18nKey={`check:status.${status}.message`} />}
        status={status}
      />
    );
  }

  /**
   * Hold everything back until the type is known — showing the universal
   * documents first and the rest a second later would read as two dialogs.
   */
  if (
    isCheckin &&
    (isPhotoCaptureEnabled || hasRestrictedAgreements) &&
    (status === "loading" || !isMemberTypeAndPhotoChecked)
  ) {
    return null;
  }

  if (pendingAgreements.length > 0) {
    return (
      <AggrementDialog
        aggrements={pendingAgreements}
        onDismiss={() => {
          navigate("/");
        }}
        onAccept={accept}
      />
    );
  }

  if (isCheckin && isPhotoCaptureEnabled) {
    if (photoCapture) {
      return (
        <PhotoDialog
          onClose={() => {
            if (photoCaptureRequired) {
              navigate(-1);
              return;
            }

            setPhotoCapture(false);
          }}
          onAccept={(photo) => {
            setPhoto(photo);
            setPhotoCapture(false);
          }}
          required={photoCaptureRequired}
        />
      );
    }
  }

  return <Scanning photo={photo} />;
};

export default PreScanning;
